import { Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { LocalChannel } from '../../../interfaces/local-channel'
import { ChannelService } from '../../../services/channel.service'
import { EventService } from '../../../services/event.service'
import { RouterService, RouterPage } from '../../../services/router.service'
import { StateService } from '../../../services/state.service'
import { ProjectCMSWidgetType } from 'htv-lib'

@Component({
  selector: 'app-channel-widget',
  standalone: true,
  imports: [],
  templateUrl: './channel-widget.component.html',
  styleUrls: ['./channel-widget.component.scss'],
})
export class ChannelWidgetComponent implements OnInit, OnDestroy {
  eventSubscription?: Subscription
  channelSubscription?: Subscription

  displayNumber = ''
  displayMessage = ''

  channelNumberInput = ''

  debounceTimer: any
  displayTimer: any

  readonly className = 'active'
  readonly debounceDelayInMs = 2000
  readonly displayDelayInMs = 2000
  readonly numberKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0']

  @ViewChild('widgetContainer') widgetContainer!: ElementRef

  constructor(
    private channelService: ChannelService,
    private eventService: EventService,
    private renderer: Renderer2,
    private routerService: RouterService,
    private translateService: TranslateService,
    private stateService: StateService
  ) {
    // do nothing
  }

  ngOnInit(): void {
    this.eventSubscription = this.eventService.keyDown.subscribe((key: string) => {
      if (this.shouldCaptureNumberKeys() && this.numberKeys.includes(key)) {
        this.debounceChannelNumberInput(key)
      }
    })
    this.channelSubscription = this.channelService.selectedChannel$.subscribe((channel?: LocalChannel) => {
      if (channel && this.isOnChannelPage()) {
        this.updateDisplay(channel.id, channel.name)
      }
    })
  }

  ngOnDestroy(): void {
    this.eventSubscription?.unsubscribe()
    this.channelSubscription?.unsubscribe()
  }

  isOnChannelPage(): boolean {
    return this.routerService.isCurrentPage(RouterPage.CHANNEL)
  }

  isOnRegisterPage(): boolean {
    return this.routerService.isCurrentPageUrl('/register')
  }

  shouldCaptureNumberKeys(): boolean {
    if (this.isOnRegisterPage()) {
      return false
    }

    const widgetsThatCaptureNumbers: ProjectCMSWidgetType[] = [ProjectCMSWidgetType.MARRIOTT_WAKE_UP_ALARM]

    for (const widgetType of widgetsThatCaptureNumbers) {
      if (this.stateService.isShowingWidget(widgetType)) {
        return false
      }
    }

    return true
  }

  updateDisplay(number: string, message: string): void {
    if (this.displayTimer) {
      clearTimeout(this.displayTimer)
    }

    const registerDisplayTimer = () => {
      this.displayTimer = setTimeout(() => {
        this.hide()
        this.resetDisplay()
      }, this.displayDelayInMs)
    }

    this.displayNumber = number
    this.displayMessage = message
    this.show()
    registerDisplayTimer()
  }

  resetDisplay(): void {
    this.hide()
    this.displayNumber = ''
    this.displayMessage = ''
  }

  debounceChannelNumberInput(num?: string): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    const registerDebounceTimer = () => {
      this.debounceTimer = setTimeout(() => {
        this.debounceChannelNumberInput()
      }, this.debounceDelayInMs)
    }

    if (num) {
      this.channelNumberInput += num
      this.updateDisplay(this.channelNumberInput, '')
      registerDebounceTimer()
    } else {
      const channel = this.channelService.getExactChannel(this.channelNumberInput)
      if (channel) {
        this.channelService.openChannelPage(channel)
      } else {
        this.updateDisplay(
          this.channelNumberInput,
          this.translateService.instant('WIDGETS.CHANNEL.CHANNEL_UNAVAILABLE')
        )
      }
      this.channelNumberInput = ''
    }
  }

  show(): void {
    this.renderer.addClass(this.widgetContainer.nativeElement, this.className)
  }

  hide(): void {
    this.renderer.removeClass(this.widgetContainer.nativeElement, this.className)
  }
}
